import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useThemeStore, type Theme } from '../store/themeStore';
import { useAuthStore } from '../store/authStore';
import { useWorkHoursStore, formatTime } from '../store/workHoursStore';
import { useOnboardingStore } from '../store/onboardingStore';
import { Button } from '../components/ui';
import { ChangelogModal } from '../components/ChangelogModal';
import { CSVImportModal, SortingGame } from '../components/contacts';
import { detectPlatform, getMessagingAppOptions, type MessagingApp } from '../utils/platform';
import { googleContactsAPI, lineupAPI, preferencesAPI, type LineupPreferences, type UserPreferences } from '../services/api';

type SubscriptionPlan = 'free' | 'pro' | 'beta';

interface GoogleConnection {
  connected: boolean;
  email: string | null;
  lastSync: number | null;
  contactCount: number;
}

// Beta access code - in production this would be validated server-side
const BETA_ACCESS_CODE = 'ONDECK2024';

export default function Settings() {
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeStore();
  const { user, logout } = useAuthStore();
  const { workHours, setWorkHours } = useWorkHoursStore();
  const { reset: resetOnboarding } = useOnboardingStore();
  const [saved, setSaved] = useState(false);

  // Show debug tools in dev/beta only
  const showDebugTools = import.meta.env.MODE !== 'production' ||
    window.location.hostname.includes('beta') ||
    window.location.hostname.includes('localhost');

  // Subscription state
  const [subscription, setSubscription] = useState<{
    plan: SubscriptionPlan;
    isBeta: boolean;
    expiresAt: string | null;
  }>(() => {
    const stored = localStorage.getItem('ondeck-subscription');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { plan: 'free', isBeta: false, expiresAt: null };
      }
    }
    return { plan: 'free', isBeta: false, expiresAt: null };
  });

  const [showBetaInput, setShowBetaInput] = useState(false);
  const [betaCode, setBetaCode] = useState('');
  const [betaError, setBetaError] = useState('');

  // Persist subscription to localStorage
  useEffect(() => {
    localStorage.setItem('ondeck-subscription', JSON.stringify(subscription));
  }, [subscription]);

  // Notification preferences (local state for now)
  const [notifications, setNotifications] = useState({
    daily_lineup_email: true,
    daily_lineup_push: true,
    contact_reminders: true,
    marketing_emails: false,
  });

  // AI preferences
  const [aiPreferences, setAiPreferences] = useState({
    enable_ai_features: true,
    ai_csv_processing: true,
    ai_message_drafting: true,
  });

  // Communication preferences
  const platformInfo = detectPlatform();
  const [communicationPrefs, setCommunicationPrefs] = useState<{
    preferred_messaging_app: MessagingApp;
    use_browser_email: boolean;
  }>(() => {
    const stored = localStorage.getItem('ondeck-communication-prefs');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {
          preferred_messaging_app: platformInfo.recommendedMessagingApp,
          use_browser_email: false,
        };
      }
    }
    return {
      preferred_messaging_app: platformInfo.recommendedMessagingApp,
      use_browser_email: false,
    };
  });

  // Persist communication prefs
  useEffect(() => {
    localStorage.setItem('ondeck-communication-prefs', JSON.stringify(communicationPrefs));
  }, [communicationPrefs]);

  // Google Contacts connection state
  const [googleConnection, setGoogleConnection] = useState<GoogleConnection>(() => {
    const stored = localStorage.getItem('ondeck-google-connection');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { connected: false, email: null, lastSync: null, contactCount: 0 };
      }
    }
    return { connected: false, email: null, lastSync: null, contactCount: 0 };
  });

  const [showChangelog, setShowChangelog] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showSortingGame, setShowSortingGame] = useState(false);
  const [importResult, setImportResult] = useState<{
    total: number;
    imported: number;
    skipped: number;
    duplicates: number;
  } | null>(null);
  const queryClient = useQueryClient();

  // Persist Google connection
  useEffect(() => {
    localStorage.setItem('ondeck-google-connection', JSON.stringify(googleConnection));
  }, [googleConnection]);

  // Fetch Google Contacts count
  const { data: googleCountData, isLoading: isLoadingCount } = useQuery({
    queryKey: ['google-contacts', 'count'],
    queryFn: () => googleContactsAPI.getCount(),
    enabled: !!user?.email, // Only fetch if user is logged in with Google
    retry: false,
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: () => googleContactsAPI.import(),
    onSuccess: (data) => {
      if (data.success && data.data) {
        setImportResult(data.data);
        setGoogleConnection(prev => ({
          ...prev,
          lastSync: Date.now(),
          contactCount: (prev.contactCount || 0) + data.data!.imported,
        }));
        // Invalidate contacts queries to refresh the list
        queryClient.invalidateQueries({ queryKey: ['contacts'] });
        // Show sorting game if contacts were imported
        if (data.data.imported > 0) {
          setShowSortingGame(true);
        }
      }
    },
  });

  const googleContactCount = googleCountData?.success ? googleCountData.data?.count : null;
  const hasGoogleConnection = googleCountData?.success && !googleCountData?.error;
  const needsReauth = googleCountData?.error?.code === 'MISSING_SCOPE' ||
                      googleCountData?.error?.code === 'NO_GOOGLE_CONNECTION';

  // Load user preferences from API
  const { data: userPrefsData } = useQuery({
    queryKey: ['user', 'preferences'],
    queryFn: () => preferencesAPI.get(),
    retry: false,
  });

  // Sync loaded preferences to local state on initial load only
  // Use a ref to track the last data version to prevent resetting user changes
  const lastSyncedDataRef = useRef<typeof userPrefsData | null>(null);

  useEffect(() => {
    // Only sync if we have new data and it's different from what we last synced
    if (userPrefsData?.success && userPrefsData.data?.preferences && userPrefsData !== lastSyncedDataRef.current) {
      const prefs = userPrefsData.data.preferences;

      // Only update if different from current (avoid loops)
      if (prefs.theme && prefs.theme !== theme) {
        setTheme(prefs.theme);
      }

      if (prefs.work_hours) {
        setWorkHours(prefs.work_hours);
      }

      if (prefs.notification_preferences) {
        setNotifications({
          daily_lineup_email: prefs.notification_preferences.email_daily_lineup ?? true,
          daily_lineup_push: prefs.notification_preferences.push_enabled ?? true,
          contact_reminders: prefs.notification_preferences.contact_reminders ?? true,
          marketing_emails: prefs.notification_preferences.marketing_emails ?? false,
        });
      }

      if (prefs.ai_preferences) {
        setAiPreferences(prefs.ai_preferences);
      }

      if (prefs.communication_preferences) {
        setCommunicationPrefs({
          preferred_messaging_app: prefs.communication_preferences.preferred_messaging_app as MessagingApp || platformInfo.recommendedMessagingApp,
          use_browser_email: prefs.communication_preferences.use_browser_email ?? false,
        });
      }

      // Mark this data as synced
      lastSyncedDataRef.current = userPrefsData;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrefsData]);

  // Lineup preferences
  const { data: lineupPrefsData, isLoading: isLoadingLineupPrefs } = useQuery({
    queryKey: ['lineup', 'preferences'],
    queryFn: () => lineupAPI.getPreferences(),
    retry: false,
  });

  const lineupPrefs: LineupPreferences = lineupPrefsData?.success
    ? lineupPrefsData.data.preferences
    : {
        max_contacts: 10,
        include_hall_of_fame: false,
        include_bench: false,
        focus_stages: [],
        minimum_days_between_contact: 3,
        prioritize_upcoming_dates: true,
        prioritize_high_value: true,
        preferred_generation_time: '06:00',
      };

  const updateLineupPrefsMutation = useMutation({
    mutationFn: (prefs: Partial<LineupPreferences>) => lineupAPI.updatePreferences(prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineup', 'preferences'] });
      // Invalidate today's lineup to regenerate with new preferences
      queryClient.invalidateQueries({ queryKey: ['lineup', 'today'] });
    },
  });

  // Save all preferences mutation
  const savePreferencesMutation = useMutation({
    mutationFn: (prefs: Partial<UserPreferences>) => preferencesAPI.update(prefs),
  });

  const handleImportGoogleContacts = () => {
    setImportResult(null);
    importMutation.mutate();
  };



  const handleBetaActivation = () => {
    if (betaCode.toUpperCase() === BETA_ACCESS_CODE) {
      setSubscription({
        plan: 'beta',
        isBeta: true,
        expiresAt: null, // Beta never expires
      });
      setShowBetaInput(false);
      setBetaCode('');
      setBetaError('');
    } else {
      setBetaError('Invalid beta code');
    }
  };

  const handleDeactivateBeta = () => {
    setSubscription({
      plan: 'free',
      isBeta: false,
      expiresAt: null,
    });
  };

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  const handleSave = async () => {
    try {
      // Save all preferences to the API
      await savePreferencesMutation.mutateAsync({
        theme,
        work_hours: workHours,
        notification_preferences: {
          email_daily_lineup: notifications.daily_lineup_email,
          email_weekly_summary: false, // Not in current UI
          push_enabled: notifications.daily_lineup_push,
          contact_reminders: notifications.contact_reminders,
          marketing_emails: notifications.marketing_emails,
        },
        ai_preferences: aiPreferences,
        communication_preferences: {
          preferred_messaging_app: communicationPrefs.preferred_messaging_app as UserPreferences['communication_preferences']['preferred_messaging_app'],
          use_browser_email: communicationPrefs.use_browser_email,
        },
      });

      // Invalidate preferences query to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['user', 'preferences'] });

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      // Still show saved for local storage (which already persisted)
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-paper-100 dark:bg-gray-900 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your account and preferences
        </p>
      </div>

      <div className="space-y-8">
        {/* Account Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{user?.name || 'User'}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email || 'email@example.com'}</p>
              </div>
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                Active
              </span>
            </div>
            <div className="pt-2">
              <button
                onClick={handleLogout}
                className="text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
              >
                Sign out of OnDeck
              </button>
            </div>
          </div>
        </section>

        {/* Connected Services Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Connected Services</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Connect your accounts to sync contacts automatically.
          </p>

          <div className="space-y-4">
            {/* Google Contacts */}
            <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Google Contacts</p>
                  {isLoadingCount ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                      Checking Google Contacts...
                    </p>
                  ) : hasGoogleConnection && googleContactCount !== null ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      <p>{user?.email}</p>
                      <p className="text-xs">
                        {googleContactCount} contacts available to import
                        {googleConnection.contactCount > 0 && ` • ${googleConnection.contactCount} already imported`}
                      </p>
                    </div>
                  ) : needsReauth ? (
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      Sign out and sign in again to grant contacts access
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Import contacts from Google
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {importMutation.isPending ? (
                  <Button size="sm" disabled>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Importing...
                  </Button>
                ) : hasGoogleConnection && googleContactCount && googleContactCount > 0 ? (
                  <Button size="sm" onClick={handleImportGoogleContacts}>
                    Import {googleContactCount} Contacts
                  </Button>
                ) : needsReauth ? (
                  <Button size="sm" variant="outline" disabled>
                    Re-auth Required
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" disabled>
                    Not Available
                  </Button>
                )}
              </div>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className="py-4 px-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  Import Complete!
                </p>
                <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                  Imported {importResult.imported} contacts
                  {importResult.duplicates > 0 && ` • ${importResult.duplicates} duplicates skipped`}
                  {importResult.skipped > 0 && ` • ${importResult.skipped} incomplete entries skipped`}
                </p>
              </div>
            )}

            {/* Import Error */}
            {importMutation.isError && (
              <div className="py-4 px-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400">
                  Failed to import contacts. Please try again.
                </p>
              </div>
            )}

            {/* Microsoft / Outlook - Coming Soon */}
            <div className="flex items-center justify-between py-4 opacity-60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#0078D4" d="M11.5 2v8.5H20V2h-8.5zM2 2v8.5h8.5V2H2zm9.5 9.5V20H20v-8.5h-8.5zm-9.5 0V20h8.5v-8.5H2z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Microsoft Outlook</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Import contacts from Outlook
                  </p>
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">
                Coming Soon
              </span>
            </div>

            {/* CSV Import */}
            <div className="flex items-center justify-between py-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Import from CSV</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Upload a spreadsheet of contacts with AI-powered field mapping
                  </p>
                </div>
              </div>
              <Button size="sm" onClick={() => setShowCSVImport(true)}>
                Import
              </Button>
            </div>
          </div>

          {/* Info about contact sync */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              When you connect a service, OnDeck will import new contacts automatically. Your contacts stay in sync with changes from the source.
            </p>
          </div>
        </section>

        {/* Subscription Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Subscription</h2>

          {/* Current Plan */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-2xl ${subscription.isBeta ? 'text-purple-600' : subscription.plan === 'pro' ? 'text-ondeck-600' : 'text-gray-600'}`}>
                {subscription.isBeta ? '⚡' : subscription.plan === 'pro' ? '⭐' : '📦'}
              </span>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">
                  {subscription.isBeta ? 'Beta Tester' : subscription.plan === 'pro' ? 'Pro Plan' : 'Free Plan'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {subscription.isBeta
                    ? 'Full access to all features including AI'
                    : subscription.plan === 'pro'
                      ? 'All features including AI lineup generation'
                      : 'Basic contact management'}
                </p>
              </div>
            </div>
            {subscription.isBeta && (
              <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  Thank you for being a beta tester! You have full access to all features.
                </p>
              </div>
            )}
          </div>

          {/* Plan Features */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Your Features</h3>
            <ul className="space-y-2">
              <FeatureItem included label="Unlimited contacts" />
              <FeatureItem included label="Contact stages & pipeline" />
              <FeatureItem included label="Smart CSV import with AI" />
              <FeatureItem included label="AI-powered contact analysis" />
              <FeatureItem included label="Daily AI lineup generation" />
              <FeatureItem included label="AI message drafting" />
              <FeatureItem included={subscription.isBeta} label="Early access to new features" />
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {!subscription.isBeta && subscription.plan === 'free' && (
              <Button className="w-full">
                Upgrade to Pro - $29/month
              </Button>
            )}

            {subscription.isBeta ? (
              <button
                onClick={handleDeactivateBeta}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              >
                Deactivate beta access
              </button>
            ) : (
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                {!showBetaInput ? (
                  <button
                    onClick={() => setShowBetaInput(true)}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-ondeck-600 dark:hover:text-ondeck-400"
                  >
                    Have a beta code?
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={betaCode}
                        onChange={(e) => setBetaCode(e.target.value)}
                        placeholder="Enter beta code"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
                        onKeyDown={(e) => e.key === 'Enter' && handleBetaActivation()}
                      />
                      <Button size="sm" onClick={handleBetaActivation}>
                        Activate
                      </Button>
                    </div>
                    {betaError && (
                      <p className="text-sm text-red-600 dark:text-red-400">{betaError}</p>
                    )}
                    <button
                      onClick={() => {
                        setShowBetaInput(false);
                        setBetaCode('');
                        setBetaError('');
                      }}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Appearance Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Appearance</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Theme
            </label>
            <div className="grid grid-cols-3 gap-3">
              <ThemeOption
                theme="light"
                currentTheme={theme}
                onClick={() => handleThemeChange('light')}
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                }
                label="Light"
                description="Always use light mode"
              />
              <ThemeOption
                theme="dark"
                currentTheme={theme}
                onClick={() => handleThemeChange('dark')}
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                }
                label="Dark"
                description="Always use dark mode"
              />
              <ThemeOption
                theme="system"
                currentTheme={theme}
                onClick={() => handleThemeChange('system')}
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
                label="System"
                description="Match your device"
              />
            </div>
          </div>
        </section>

        {/* Work Hours Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Work Hours</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Set your regular working hours for personalized greetings and reminders.
          </p>

          {/* Time Range */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Daily Schedule
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <select
                  value={workHours.startHour}
                  onChange={(e) => setWorkHours({ startHour: parseInt(e.target.value) })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-ondeck-500"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {formatTime(i, 0).split(':')[0]} {i >= 12 ? 'PM' : 'AM'}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500 dark:text-gray-400">:</span>
                <select
                  value={workHours.startMinute}
                  onChange={(e) => setWorkHours({ startMinute: parseInt(e.target.value) })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-ondeck-500"
                >
                  <option value={0}>00</option>
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                </select>
              </div>

              <span className="text-gray-500 dark:text-gray-400 font-medium">to</span>

              <div className="flex items-center gap-2">
                <select
                  value={workHours.endHour}
                  onChange={(e) => setWorkHours({ endHour: parseInt(e.target.value) })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-ondeck-500"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {formatTime(i, 0).split(':')[0]} {i >= 12 ? 'PM' : 'AM'}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500 dark:text-gray-400">:</span>
                <select
                  value={workHours.endMinute}
                  onChange={(e) => setWorkHours({ endMinute: parseInt(e.target.value) })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-ondeck-500"
                >
                  <option value={0}>00</option>
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Currently set: {formatTime(workHours.startHour, workHours.startMinute)} - {formatTime(workHours.endHour, workHours.endMinute)}
            </p>
          </div>

          {/* Work Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Work Days
            </label>
            <div className="flex flex-wrap gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                <button
                  key={day}
                  onClick={() => {
                    const newDays = workHours.workDays.includes(index)
                      ? workHours.workDays.filter(d => d !== index)
                      : [...workHours.workDays, index].sort();
                    setWorkHours({ workDays: newDays });
                  }}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    workHours.workDays.includes(index)
                      ? 'bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-300 border-2 border-ondeck-500'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="mt-4 p-3 bg-ondeck-50 dark:bg-ondeck-900/20 rounded-lg">
            <p className="text-sm text-ondeck-700 dark:text-ondeck-300">
              During your work hours, you'll see encouraging messages on your dashboard to keep you motivated!
            </p>
          </div>
        </section>

        {/* Notifications Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notifications</h2>
          <div className="space-y-4">
            <ToggleSetting
              label="Daily lineup email"
              description="Receive your daily lineup via email each morning"
              checked={notifications.daily_lineup_email}
              onChange={(v) => setNotifications(prev => ({ ...prev, daily_lineup_email: v }))}
            />
            <ToggleSetting
              label="Daily lineup push notification"
              description="Get a push notification when your lineup is ready"
              checked={notifications.daily_lineup_push}
              onChange={(v) => setNotifications(prev => ({ ...prev, daily_lineup_push: v }))}
            />
            <ToggleSetting
              label="Contact reminders"
              description="Reminders for scheduled follow-ups and birthdays"
              checked={notifications.contact_reminders}
              onChange={(v) => setNotifications(prev => ({ ...prev, contact_reminders: v }))}
            />
            <ToggleSetting
              label="Marketing emails"
              description="Product updates and tips from OnDeck"
              checked={notifications.marketing_emails}
              onChange={(v) => setNotifications(prev => ({ ...prev, marketing_emails: v }))}
            />
          </div>
        </section>

        {/* AI Features Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">AI Features</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Control how AI is used in your account. Your data is processed securely by Google Gemini.
          </p>
          <div className="space-y-4">
            <ToggleSetting
              label="Enable AI features"
              description="Daily lineup generation, relationship scoring, and insights"
              checked={aiPreferences.enable_ai_features}
              onChange={(v) => setAiPreferences(prev => ({ ...prev, enable_ai_features: v }))}
            />
            <ToggleSetting
              label="AI CSV processing"
              description="Use AI to intelligently parse and clean imported contacts"
              checked={aiPreferences.ai_csv_processing}
              onChange={(v) => setAiPreferences(prev => ({ ...prev, ai_csv_processing: v }))}
              disabled={!aiPreferences.enable_ai_features}
            />
            <ToggleSetting
              label="AI message drafting"
              description="Get AI help drafting emails and messages to contacts"
              checked={aiPreferences.ai_message_drafting}
              onChange={(v) => setAiPreferences(prev => ({ ...prev, ai_message_drafting: v }))}
              disabled={!aiPreferences.enable_ai_features}
            />
          </div>
        </section>

        {/* Lineup Preferences Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Daily Lineup</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Customize how your daily lineup is generated.
          </p>

          {isLoadingLineupPrefs ? (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-6 w-6 text-ondeck-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Max Contacts Slider */}
              <div className="py-3 border-b border-gray-100 dark:border-gray-700">
                <label className="block font-medium text-gray-900 dark:text-white mb-1">
                  Contacts Per Lineup
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  How many contacts to include in your daily lineup ({lineupPrefs.max_contacts} contacts)
                </p>
                <input
                  type="range"
                  min="5"
                  max="20"
                  value={lineupPrefs.max_contacts}
                  onChange={(e) => updateLineupPrefsMutation.mutate({ max_contacts: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-ondeck-600"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>5 (focused)</span>
                  <span>20 (comprehensive)</span>
                </div>
              </div>

              {/* Minimum Days Between Contact */}
              <div className="py-3 border-b border-gray-100 dark:border-gray-700">
                <label className="block font-medium text-gray-900 dark:text-white mb-1">
                  Minimum Days Between Contacts
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Skip contacts you've already reached out to recently
                </p>
                <select
                  value={lineupPrefs.minimum_days_between_contact}
                  onChange={(e) => updateLineupPrefsMutation.mutate({ minimum_days_between_contact: parseInt(e.target.value) })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-ondeck-500"
                >
                  <option value={0}>No minimum (show all)</option>
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>1 week</option>
                  <option value={14}>2 weeks</option>
                  <option value={30}>1 month</option>
                </select>
              </div>

              {/* Toggle Settings */}
              <ToggleSetting
                label="Include past clients (Hall of Fame)"
                description="Add closed deals and past clients to your lineup for follow-ups"
                checked={lineupPrefs.include_hall_of_fame}
                onChange={(v) => updateLineupPrefsMutation.mutate({ include_hall_of_fame: v })}
              />
              <ToggleSetting
                label="Include inactive contacts (Bench)"
                description="Add cold or dormant leads to your lineup for re-engagement"
                checked={lineupPrefs.include_bench}
                onChange={(v) => updateLineupPrefsMutation.mutate({ include_bench: v })}
              />
              <ToggleSetting
                label="Prioritize upcoming dates"
                description="Boost contacts with birthdays or anniversaries coming up"
                checked={lineupPrefs.prioritize_upcoming_dates}
                onChange={(v) => updateLineupPrefsMutation.mutate({ prioritize_upcoming_dates: v })}
              />
              <ToggleSetting
                label="Prioritize high-value deals"
                description="Boost contacts with larger budgets or deal values"
                checked={lineupPrefs.prioritize_high_value}
                onChange={(v) => updateLineupPrefsMutation.mutate({ prioritize_high_value: v })}
              />
            </div>
          )}

          {/* Info */}
          <div className="mt-4 p-3 bg-ondeck-50 dark:bg-ondeck-900/20 rounded-lg">
            <p className="text-sm text-ondeck-700 dark:text-ondeck-300">
              Changes take effect on your next lineup generation. Refresh your dashboard to generate a new lineup with these preferences.
            </p>
          </div>
        </section>

        {/* Communication Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Communication</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Configure how you send messages and emails to contacts.
          </p>

          {/* Platform Detection Banner */}
          <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Detected: <strong className="capitalize">{platformInfo.platform}</strong> —
              Recommended: <strong>{platformInfo.messagingAppLabel}</strong>
            </p>
          </div>

          <div className="space-y-4">
            {/* Messaging App Preference */}
            <div className="py-3 border-b border-gray-100 dark:border-gray-700">
              <label className="block font-medium text-gray-900 dark:text-white mb-1">
                Preferred Messaging App
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                When you click "Message" on a contact, OnDeck will open this app
              </p>
              <div className="grid grid-cols-2 gap-3">
                {getMessagingAppOptions().map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setCommunicationPrefs(prev => ({ ...prev, preferred_messaging_app: option.value as MessagingApp }))}
                    className={`flex flex-col items-start p-3 rounded-lg border-2 transition-all text-left ${
                      communicationPrefs.preferred_messaging_app === option.value
                        ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <span className={`text-sm font-medium ${
                      communicationPrefs.preferred_messaging_app === option.value
                        ? 'text-ondeck-700 dark:text-ondeck-300'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {option.label}
                      {option.value === platformInfo.recommendedMessagingApp && (
                        <span className="ml-2 text-xs text-ondeck-600 dark:text-ondeck-400">(Recommended)</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {option.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Email Method Toggle */}
            <ToggleSetting
              label="Open email in web browser"
              description="When OFF: Uses mailto: links (opens your default email app). When ON: Opens Gmail/Outlook in a browser tab."
              checked={communicationPrefs.use_browser_email}
              onChange={(v) => setCommunicationPrefs(prev => ({ ...prev, use_browser_email: v }))}
            />
          </div>

          {/* Info about manual logging */}
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>Tip:</strong> After messaging a contact, use "Log Conversation" to paste and save the conversation thread for tracking.
            </p>
          </div>
        </section>

        {/* Help & Onboarding Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Help & Setup</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Restart Onboarding</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Walk through the setup wizard again to update your preferences
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={resetOnboarding}>
                Restart
              </Button>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Help & Documentation</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Learn how to get the most out of OnDeck
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => window.open('/help', '_blank')}>
                View Help
              </Button>
            </div>
            {/* Debug Tools - only in dev/beta */}
            {showDebugTools && (
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Debug Tools</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Development utilities for testing and data reset
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/debug')}>
                  Open
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Save Button */}
        <div className="pt-4">
          <Button onClick={handleSave}>
            {saved ? '✓ Saved!' : 'Save Changes'}
          </Button>
        </div>

        {/* Version */}
        <div className="pt-8 pb-4 text-center">
          <button
            onClick={() => setShowChangelog(true)}
            className="text-xs text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
          >
            v{import.meta.env.VITE_APP_VERSION || '0.0.0'}
          </button>
        </div>
      </div>

      {/* Changelog Modal */}
      <ChangelogModal open={showChangelog} onClose={() => setShowChangelog(false)} />

      {/* CSV Import Modal */}
      <CSVImportModal
        open={showCSVImport}
        onClose={() => setShowCSVImport(false)}
        onImportComplete={() => {
          setShowCSVImport(false);
          setShowSortingGame(true);
        }}
      />

      {/* Sorting Game Modal (after Google Contacts or CSV import) */}
      <SortingGame
        open={showSortingGame}
        onClose={() => {
          setShowSortingGame(false);
          navigate('/contacts');
        }}
      />
    </div>
  );
}

// Theme option card component
function ThemeOption({
  theme,
  currentTheme,
  onClick,
  icon,
  label,
  description,
}: {
  theme: Theme;
  currentTheme: Theme;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  description: string;
}) {
  const isSelected = theme === currentTheme;

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
        isSelected
          ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/30'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      <div className={`mb-2 ${isSelected ? 'text-ondeck-600 dark:text-ondeck-400' : 'text-gray-500 dark:text-gray-400'}`}>
        {icon}
      </div>
      <span className={`text-sm font-medium ${isSelected ? 'text-ondeck-700 dark:text-ondeck-300' : 'text-gray-900 dark:text-white'}`}>
        {label}
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
        {description}
      </span>
    </button>
  );
}

// Toggle setting component
function ToggleSetting({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 ${disabled ? 'opacity-50' : ''}`}>
      <div>
        <p className="font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-ondeck-600' : 'bg-gray-300 dark:bg-gray-600'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

// Feature item component for subscription features list
function FeatureItem({ included, label }: { included: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      {included ? (
        <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      <span className={included ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}>
        {label}
      </span>
    </li>
  );
}
